import { useEffect, useState } from 'react';
import { useForm } from '@abyss/web/hooks/useForm';
import { useRouter } from '@abyss/web/hooks/useRouter';
import { useToast } from '@abyss/web/hooks/useToast';
import { AlertService } from '@src/api/common/AlertService';
import { AlertVariantType } from '@src/constants/AlertVariantType';
import { ApiStatus } from '@src/constants/ApiStatus';
import { UrlService } from '@src/api/common/UrlService';
import { EmployeeService } from '@src/api/web-services/SampleService/EmployeeService';
import { PageTitleService } from '@src/api/common/PageTitleService';
import { BreadcrumbService } from '@src/api/common/BreadcrumbService';
import { useDataTable } from '@abyss/web/hooks/useDataTable';
import { Breadcrumbs, Title } from './PageInfo';

export const DataTableExampleController = () => {
  const { AlertModel, ShowAlert } = AlertService();
  const { getRouteParams, navigate } = useRouter();
  const { toast } = useToast();
  const params = getRouteParams('');
  const { GetEmployeeList } = EmployeeService();
  const { UpdatePageTitle } = PageTitleService();
  const { UpdateBreadcrumb } = BreadcrumbService();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const form = useForm({
    defaultValues: {
      name: '',
      department: '',
    },
  });

  // Define columns for the DataTable
  const columns = [
    {
      Header: 'ID',
      accessor: 'id',
      width: 80,
    },
    {
      Header: 'Employee Name',
      accessor: 'name',
      width: 200,
    },
    {
      Header: 'Email',
      accessor: 'email',
      width: 250,
    },
    {
      Header: 'Department',
      accessor: 'department',
      width: 150,
    },
    {
      Header: 'Position',
      accessor: 'position',
      width: 180,
    },
    {
      Header: 'Age',
      accessor: 'age',
      width: 80,
    },
    {
      Header: 'Salary',
      accessor: 'salary',
      width: 120,
      Cell: ({ value }: any) => `$${value.toLocaleString()}`,
    },
    {
      Header: 'Created Date',
      accessor: 'createdDate',
      width: 120,
    },
    {
      Header: 'Created By',
      accessor: 'createdBy',
      width: 120,
    },
    {
      Header: 'Updated Date',
      accessor: 'updatedDate',
      width: 120,
    },
    {
      Header: 'Updated By',
      accessor: 'updatedBy',
      width: 120,
    },
  ];

  // Configure DataTable using useDataTable hook
  const tableState = useDataTable({
    initialData: data,
    initialColumns: columns,
    showPagination: true,
    showGlobalFilter: true,
    showFilterDataset: true,
    showColumnVisibilityConfig: true,
    showTableSettings: true,
    showDownloadButton: true,
    pageSizeOptions: [5, 10, 25, 50],
    pageSizeDefault: 10,
  });

  const loadEmployeeData = async (formData: any = null) => {
    setLoading(true);
    try {
      const body = formData !== null ? formData.getValues() : form.getValues();
      const queryString = UrlService.BuildQuery(body);
      const apiData: any = await GetEmployeeList(queryString);

      if (
        apiData.status === ApiStatus.Success ||
        apiData.status === 'success'
      ) {
        setData(apiData.data.results);
        tableState.setData(apiData.data.results);
      } else {
        AlertModel.variant = AlertVariantType.ERROR;
        AlertModel.message = apiData.message || 'Failed to load employee data';
        AlertModel.visible = true;
        AlertModel.isPermanent = true;
        await ShowAlert(AlertModel);
      }
    } catch (error) {
      AlertModel.variant = AlertVariantType.ERROR;
      AlertModel.message = 'An error occurred while loading employee data';
      AlertModel.visible = true;
      AlertModel.isPermanent = true;
      await ShowAlert(AlertModel);
    } finally {
      setLoading(false);
    }
  };

  const Action = {
    Init: async () => {
      await UpdatePageTitle(Title);
      await UpdateBreadcrumb(Breadcrumbs);
      await loadEmployeeData();
    },
    FilterData: async () => {
      await loadEmployeeData(form);
      // Show success toast notification
      toast.show({
        title: 'Success',
        message: 'Filtered Successfully',
        variant: 'success',
        autoClose: 3000,
      });
    },
    ResetFilters: async () => {
      form.reset();
      await loadEmployeeData();
    },
    OnCreateNew: async () => {
      navigate('/employee/0');
    },
  };

  useEffect(() => {
    Action.Init();
  }, []);

  return {
    form,
    tableState,
    params,
    Action,
    loading,
    data,
  };
};
